import { Injectable } from '@angular/core';
import { ComponentBase } from '../component-base';
import { Select3dService } from './select-3d.service';
import { RailingGenerationService } from './railing/services/railing-generation.service';
import { StageService } from './stage.service';
import { SceneUtilsService } from './scene-utils.service';
import { AppSettingsService } from './app-settings.service';
import { MoveCameraService } from '../Component(2D and 3D)/2D/move-camera.service';
import { ConstantsService } from './constants.service';
import { EventLogService, Actions } from './eventLog';
import { StairsDimensionService } from './stairs-dimension.service';
import { AutoScaleSprite } from '../custom/AutoScaleSprite';
import { ProductsService } from './products.service';
import { CameraService } from './camera.service';
import { Mobile3dResizeOrbitLockService } from './mobile-3d-resize-orbit-lock.service';

declare let THREE: any;

@Injectable()
export class MoveStairsService extends ComponentBase {

  widthPlane: any;
  newPosition: any;
  oldPosition: any;
  currentSprite: any;
  plane: any;
  selectedStair: any;
  stairDirectionType: string;

  selectStairSubscription: any;
  stairsTransformAction: Actions.ChangeStairsTransformationAction;

  constructor(
    private stage: StageService,
    private sceneUtils: SceneUtilsService,
    private railingGeneration: RailingGenerationService,
    private select3dService: Select3dService,
    private appSettingsService: AppSettingsService,
    private moveCameraService: MoveCameraService,
    private constants: ConstantsService,
    private undoSrvc: EventLogService,
    private stairDimensionsSrvc: StairsDimensionService,
    private productsService: ProductsService,
    private cameraService: CameraService,
    private mobile3dResizeOrbitLock: Mobile3dResizeOrbitLockService
  ) {
    super();
    this.disable();
  }

  sprites: any = [];
  widthSprites = [];
  loader = new THREE.TextureLoader();
  raycaster = new THREE.Raycaster();
  minimalSpaceForStairsOnSIde: number = 0;

  /** Тач: отслеживаем палец; второй палец не начинает ресайз. */
  private _stairResizeTouchId: number | null = null;
  private _lastTouchOffsetX = 0;
  private _lastTouchOffsetY = 0;
  /** После touchend браузер шлёт синтетический mousedown — один раз игнорируем. */
  private _suppressNextMouseDownAfterTouch = false;
  /** Синхронизация с Mobile3dResizeOrbitLockService при видимых стрелках в 3D на touch. */
  private _mobileStairsResizeCameraLockActive = false;

  private _patchOffsetFromTouch(e: TouchEvent, touch: Touch): void {
    const target = e.target as HTMLElement;
    if (!target || !touch || !target.getBoundingClientRect) {
      return;
    }
    const rect = target.getBoundingClientRect();
    (e as any).offsetX = touch.clientX - rect.left;
    (e as any).offsetY = touch.clientY - rect.top;
  }

  private _touchById(list: TouchList, id: number): Touch | null {
    if (!list || list.length === 0) {
      return null;
    }
    for (let i = 0; i < list.length; i++) {
      if (list[i].identifier === id) {
        return list[i];
      }
    }
    return null;
  }

  touchStart(e: TouchEvent) {
    if (!this.isEnabled() || !e.changedTouches || e.changedTouches.length === 0) {
      return;
    }
    if (!e.targetTouches || e.targetTouches.length !== 1) {
      return;
    }
    const t = e.changedTouches[0];
    this._stairResizeTouchId = t.identifier;
    this._patchOffsetFromTouch(e, t);
    this._lastTouchOffsetX = (e as any).offsetX;
    this._lastTouchOffsetY = (e as any).offsetY;

    this.mouseDown(e as any);

    if (this.currentSprite) {
      if (e.cancelable) {
        e.preventDefault();
      }
      if ((e as any).stopPropagation) {
        (e as any).stopPropagation();
      }
      return false;
    }
  }

  touchMove(e: TouchEvent) {
    if (!this.isEnabled() || this._stairResizeTouchId === null || !this.currentSprite) {
      return;
    }
    const touch = this._touchById(e.targetTouches, this._stairResizeTouchId);
    if (!touch) {
      return;
    }
    this._patchOffsetFromTouch(e, touch);
    this._lastTouchOffsetX = (e as any).offsetX;
    this._lastTouchOffsetY = (e as any).offsetY;
    this.mouseMove(e as any);
    if (e.cancelable) {
      e.preventDefault();
    }
    if ((e as any).stopPropagation) {
      (e as any).stopPropagation();
    }
    return false;
  }

  touchEnd(e: TouchEvent) {
    if (this._stairResizeTouchId === null) {
      return;
    }
    let endsResizeFinger = false;
    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier === this._stairResizeTouchId) {
        endsResizeFinger = true;
        break;
      }
    }
    if (!endsResizeFinger) {
      return;
    }
    this._stairResizeTouchId = null;
    this._suppressNextMouseDownAfterTouch = true;
    (e as any).offsetX = this._lastTouchOffsetX;
    (e as any).offsetY = this._lastTouchOffsetY;
    const hadActiveResize = !!this.currentSprite;
    this.mouseUp(e as any);
    if (hadActiveResize) {
      if (e.cancelable) {
        e.preventDefault();
      }
      if ((e as any).stopPropagation) {
        (e as any).stopPropagation();
      }
      return false;
    }
  }

  drawSprites(selectedStair?) {
    const stair = selectedStair ? selectedStair : this.select3dService.getSelectedStair();
    this.selectedStair = stair;
    const config = stair.userData.config;

    const cornerPost = this.productsService.getCurrentCornerPost();

    this.minimalSpaceForStairsOnSIde = cornerPost.mountType && cornerPost.mountType.includes('Fascia')? this.constants.minimalSpaceForStairsOnSIde : 0;

    const spriteMap = this.loader.load('../../assets/images/sprites/size_grip_horizontal.png');
    this.stage.remove(this.plane);

    const spriteMaterial = new THREE.SpriteMaterial({ map: spriteMap, color: 0xffffff, depthTest: false, depthWrite: false });

    const floor = this.stage.getFloorById(stair.userData.config.floorId);
    const height = floor.getWorldPosition(new THREE.Vector3()).y + 5;

    const stairPosition = new THREE.Vector3(
      stair.userData.config.firstPoint.x,
      height,
      stair.userData.config.firstPoint.y
    );

    const edgeVector = new THREE.Vector3(
      stair.userData.config.secondPoint.x - stair.userData.config.firstPoint.x,
      0,
      stair.userData.config.secondPoint.y - stair.userData.config.firstPoint.y
    );

    let spriteRotation = Math.PI;
    if (this.appSettingsService.is2D) {
      if (config.secondPoint.x > config.firstPoint.x) {
        spriteRotation = Math.PI;
        spriteRotation = Math.PI - stair.children[0].rotation.y;
      } else {
        spriteRotation = stair.children[0].rotation.y;
      }
    }

    edgeVector.setLength(stair.userData.config.distance);
    stairPosition.add(edgeVector);

    const topSprite = new AutoScaleSprite(50, 50, spriteMaterial);
    topSprite.userData.stair = stair;
    topSprite.position.set(stairPosition.x, stairPosition.y, stairPosition.z);
    topSprite.material.rotation = spriteRotation;
    this.sprites.push(topSprite);
    topSprite.name = 'Top sprite';
    this.stage.add(topSprite);

    this.plane = new THREE.Mesh(new THREE.PlaneGeometry(5000, 5000, 8, 8),
      new THREE.MeshBasicMaterial({
        color: 0x248f24, alphaTest: 1, side: THREE.DoubleSide, visible: false
      }));
    this.plane.position.set(stairPosition.x, stairPosition.y, stairPosition.z);
    this.plane.rotation.set(stair.children[0].rotation.x, stair.children[0].rotation.y, stair.children[0].rotation.z);
    this.stage.add(this.plane);
    if (!this.appSettingsService.is2D) {
    } else {
      this.plane.rotation.x = Math.PI / 2;
      this.plane.rotation.y = 0;
    }

    if (this.selectedStair.userData.type === 'Straight') {
      const vector = new THREE.Vector3(edgeVector.z, 0, -edgeVector.x);
      vector.setLength((height / 14) * 11);
      const down = new THREE.Vector3(0, -height / 2, 0);
      edgeVector.setLength(stair.userData.config.width / 2);

      const rightSprite = new AutoScaleSprite(50, 50, spriteMaterial);
      rightSprite.userData.stair = stair;
      rightSprite.position.set(stairPosition.x, stairPosition.y, stairPosition.z);
      rightSprite.position.add(vector);
      rightSprite.position.add(down);
      rightSprite.position.add(edgeVector);
      this.widthSprites.push(rightSprite);
      rightSprite.name = 'Right sprite';
      this.stage.add(rightSprite);

      edgeVector.negate();

      const leftSprite = new AutoScaleSprite(50, 50, spriteMaterial);
      leftSprite.userData.stair = stair;
      leftSprite.position.set(stairPosition.x, stairPosition.y, stairPosition.z);
      leftSprite.position.add(vector);
      leftSprite.position.add(down);
      leftSprite.position.add(edgeVector);
      this.widthSprites.push(leftSprite);
      leftSprite.name = 'Left sprite';
      this.stage.add(leftSprite);

      this.widthPlane = new THREE.Mesh(new THREE.PlaneGeometry(5000, 5000, 8, 8),
        new THREE.MeshBasicMaterial({
          color: 0xff0000, alphaTest: 1, side: THREE.DoubleSide, visible: false
        }));
      this.widthPlane.position.set(leftSprite.position.x, leftSprite.position.y, leftSprite.position.z);
      this.widthPlane.rotation.set(stair.children[0].rotation.x, stair.children[0].rotation.y, stair.children[0].rotation.z);
      this.stage.add(this.widthPlane);

      if (this.widthPlane.rotation.y >= 0 && this.widthPlane.rotation.y < 1.5) {
        this.stairDirectionType = 'Vertical';
      } else {
        this.stairDirectionType = 'Horizontal';
      }

      if (!this.appSettingsService.is2D) {
      } else {
        this.plane.rotation.x = Math.PI / 2;
        this.plane.rotation.y = 0;
        this.widthPlane.rotation.y = 0;
        this.widthPlane.rotation.x = Math.PI / 2;
      }
      // this.plane.rotation.x = Math.PI / 2;
      // this.widthPlane.rotation.x = Math.PI / 2;
    }
    this._syncMobile3dStairsResizeCameraLock();
  }

  removeSprites(skipResizeCameraLockSync?: boolean) {
    this.stage.removeMany(this.widthSprites);
    this.stage.removeMany(this.sprites);
    this.widthSprites = [];
    this.sprites = [];
    this.stage.remove(this.plane);
    this.stage.remove(this.widthPlane);
    if (!skipResizeCameraLockSync) {
      this._syncMobile3dStairsResizeCameraLock();
    }
  }

  private _syncMobile3dStairsResizeCameraLock() {
    const hasArrows = this.sprites.length > 0 || this.widthSprites.length > 0;
    const want =
      hasArrows &&
      this.cameraService.isIn3D() &&
      typeof navigator !== 'undefined' &&
      navigator.maxTouchPoints > 0;
    if (want === this._mobileStairsResizeCameraLockActive) {
      return;
    }
    this._mobileStairsResizeCameraLockActive = want;
    if (want) {
      this.mobile3dResizeOrbitLock.begin();
    } else {
      this.mobile3dResizeOrbitLock.end();
    }
  }

  mouseDown(e) {
    // После touchend некоторые браузеры генерируют синтетический mousedown.
    // Важно: не блокировать touchStart → mouseDown делегацию (там e.type === 'touchstart').
    if (this._suppressNextMouseDownAfterTouch && e && e.type === 'mousedown') {
      this._suppressNextMouseDownAfterTouch = false;
      return;
    }

    const p = this.sceneUtils.getRay(e.offsetX, e.offsetY);
    this.raycaster.ray.origin.copy(p.ray.origin);
    this.raycaster.ray.direction.copy(p.ray.direction);

    let intersectionPos: any = null;

    const intersection = this.raycaster.intersectObjects([...this.widthSprites, ...this.sprites]);
    if (intersection.length) {
      this.currentSprite = intersection[0].object;
      const stairs = this.currentSprite.userData.stair;
      const config = stairs.userData.config;

      this.stairsTransformAction = new Actions.ChangeStairsTransformationAction(
        stairs.children
          .map(child => ({
            position: child.position.clone(),
            scale: child.scale.clone()
          })),
        { ...config, firstPoint: { ...config.firstPoint }, secondPoint: { ...config.secondPoint } }
      );
      let intersectionPlane;
      this.moveCameraService.disable();

      // if (!this.appSettingsService.is2D) {
      intersectionPlane = this.raycaster.intersectObjects([this.plane]);
      intersectionPos = intersectionPlane[0].point;
      // }
      // else {
      //   this.moveCameraService.disable();
      //   let normal = this.plane.getWorldDirection(new THREE.Vector3()).applyMatrix4( this.plane.matrixWorld );
      //   intersectionPos = intersection[0].point.projectOnPlane(normal);
      // }


    } else {
      this.currentSprite = null;
      this.removeSprites();
    }

    this.oldPosition = intersectionPos ? intersectionPos : null;
  }

  mouseMove(e) {
    if (this.currentSprite) {
      // this.orbit.disable();
      e.stopPropagation();
      if (this.currentSprite.name === 'Top sprite') {
        this.selectedStair = this.currentSprite.userData.stair;
        // this.currentFloor = this.currentSprite.parent;
        const config = this.selectedStair.userData.config;
        this.currentSprite.userData.stair = this.selectedStair;

        const p = this.sceneUtils.getRay(e.offsetX, e.offsetY);
        this.raycaster.ray.origin.copy(p.ray.origin);
        this.raycaster.ray.direction.copy(p.ray.direction);

        const intersectionPlane = this.raycaster.intersectObjects([this.plane]);
        const intersectionPos = intersectionPlane[0].point;
        this.newPosition = intersectionPos;

        const move = new THREE.Vector3(this.newPosition.x - config.firstPoint.x, 0, this.newPosition.z - config.firstPoint.y);
        const direction = new THREE.Vector3(
          this.selectedStair.userData.config.secondPoint.x - this.selectedStair.userData.config.firstPoint.x,
          0,
          this.selectedStair.userData.config.secondPoint.y - this.selectedStair.userData.config.firstPoint.y
        );
        const shift = move.projectOnVector(direction);
        // shift.setLength(shift.length() / 2);

        const distance = shift.length();

        // if (shift.x * direction.x + shift.z * direction.z > 0) {
        //   config.distance += shift.length();
        // } else {
        //   config.distance -= shift.length();
        // }

        const edgeLength = Math.sqrt(
          Math.pow((config.firstPoint.x - config.secondPoint.x), 2) +
          Math.pow((config.firstPoint.y - config.secondPoint.y), 2)
        );

        if (
          !((distance > edgeLength - this.minimalSpaceForStairsOnSIde - config.width / 2) ||
            (distance < config.width / 2 + this.minimalSpaceForStairsOnSIde) ||
            (shift.x * direction.x + shift.z * direction.z < 0))
        ) {

          const floor = this.stage.getFloorById(this.selectedStair.userData.config.floorId);
          const height = floor.getWorldPosition(new THREE.Vector3()).y + 5;
          const stairPosition = new THREE.Vector3(
            this.selectedStair.userData.config.firstPoint.x,
            height,
            this.selectedStair.userData.config.firstPoint.y
          );
          const edgeVector = new THREE.Vector3(
            this.selectedStair.userData.config.secondPoint.x - this.selectedStair.userData.config.firstPoint.x,
            0,
            this.selectedStair.userData.config.secondPoint.y - this.selectedStair.userData.config.firstPoint.y
          );

          this.selectedStair.userData.config.distance = shift.length();
          edgeVector.setLength(this.selectedStair.userData.config.distance);
          stairPosition.add(edgeVector);
          const oldSpritePosition = new THREE.Vector3(
            this.currentSprite.position.x,
            this.currentSprite.position.y,
            this.currentSprite.position.z
          );
          this.currentSprite.position.set(stairPosition.x, stairPosition.y, stairPosition.z);
          const spriteMove = new THREE.Vector3(
            this.currentSprite.position.x - oldSpritePosition.x,
            this.currentSprite.position.y - oldSpritePosition.y,
            this.currentSprite.position.z - oldSpritePosition.z
          );

          if (this.widthSprites.length) {
            this.widthSprites[0].position.add(spriteMove);
            this.widthSprites[1].position.add(spriteMove);
          }

          for (let i = 0; i < this.selectedStair.children.length; i++) {
            this.selectedStair.children[i].position.add(spriteMove);
          }
          this.oldPosition = this.newPosition;
        }

      } else {
        const floor = this.stage.getFloorById(this.selectedStair.userData.config.floorId);
        const config = this.selectedStair.userData.config;
        //this.selectedStair = floor.getObjectById(config.firstPoint.superstructureId);
        this.selectedStair = this.currentSprite.userData.stair;
        this.currentSprite.userData.stair = this.selectedStair;

        const p = this.sceneUtils.getRay(e.offsetX, e.offsetY);
        this.raycaster.ray.origin.copy(p.ray.origin);
        this.raycaster.ray.direction.copy(p.ray.direction);

        //const stairPosition = this.selectedStair.getWorldPosition(new THREE.Vector3());

        const intersectionPlane = this.raycaster.intersectObjects([this.widthPlane]);

        const intersectionPos = intersectionPlane[0].point;
        this.newPosition = intersectionPos;

        const move = new THREE.Vector3(
          this.newPosition.x - this.oldPosition.x,
          this.newPosition.y - this.oldPosition.y,
          this.newPosition.z - this.oldPosition.z
        );
        const direction = new THREE.Vector3(
          this.selectedStair.userData.config.secondPoint.x - this.selectedStair.userData.config.firstPoint.x,
          0,
          this.selectedStair.userData.config.secondPoint.y - this.selectedStair.userData.config.firstPoint.y
        );
        const shift = move.projectOnVector(direction);
        shift.setLength(shift.length() / 2);

        // let edgeVector = new THREE.Vector3(
        //   this.selectedStair.userData.config.secondPoint.x - this.selectedStair.userData.config.firstPoint.x,
        //   0,
        //   this.selectedStair.userData.config.secondPoint.y - this.selectedStair.userData.config.firstPoint.y
        // );

        // edgeVector.setLength(this.selectedStair.userData.config.distance);
        // stairPosition.add(edgeVector);

        const oldSpritePosition = new THREE.Vector3(
          this.currentSprite.position.x,
          this.currentSprite.position.y,
          this.currentSprite.position.z
        );
        let spriteMove;
        const deltaX = (config.firstPoint.y - config.secondPoint.y) / (config.firstPoint.x - config.secondPoint.x);
        const deltaZ = (config.firstPoint.x - config.secondPoint.x) / (config.firstPoint.y - config.secondPoint.y);
        if (this.appSettingsService.is2D) {
          if (this.stairDirectionType === 'Vertical') {
            this.currentSprite.position.set(
              this.newPosition.x,
              this.currentSprite.position.y,
              (this.newPosition.x - this.currentSprite.position.x) * deltaX + this.currentSprite.position.z
            );
            spriteMove = new THREE.Vector3(
              this.currentSprite.position.x - oldSpritePosition.x,
              this.currentSprite.position.y - oldSpritePosition.y,
              this.currentSprite.position.z - oldSpritePosition.z
            );
          } else if (this.stairDirectionType === 'Horizontal') {
            const x = this.newPosition.x * deltaX;
            this.currentSprite.position.set(
              (this.newPosition.z - this.currentSprite.position.z) * deltaZ + this.currentSprite.position.x,
              this.currentSprite.position.y,
              this.newPosition.z
            );
            spriteMove = new THREE.Vector3(
              this.currentSprite.position.x - oldSpritePosition.x,
              this.currentSprite.position.y - oldSpritePosition.y,
              this.currentSprite.position.z - oldSpritePosition.z
            );
          }
        } else {
          this.currentSprite.position.set(
            this.newPosition.x,
            this.currentSprite.position.y,
            this.newPosition.z
          );
          spriteMove = new THREE.Vector3(
            this.currentSprite.position.x - oldSpritePosition.x,
            this.currentSprite.position.y - oldSpritePosition.y,
            this.currentSprite.position.z - oldSpritePosition.z
          );
        }


        if (spriteMove.x * direction.x + spriteMove.z * direction.z > 0) {
          this.selectedStair.userData.config.distance += spriteMove.length() / 2;
        } else {
          this.selectedStair.userData.config.distance -= spriteMove.length() / 2;
        }

        switch (this.currentSprite.name) {
          case 'Left sprite': {
            if (spriteMove.x * direction.x + spriteMove.z * direction.z > 0) {
              this.selectedStair.userData.config.width -= spriteMove.length();
            } else {
              this.selectedStair.userData.config.width += spriteMove.length();
            }
          }
            break;
          case 'Right sprite': {
            if (spriteMove.x * direction.x + spriteMove.z * direction.z > 0) {
              this.selectedStair.userData.config.width += spriteMove.length();
            } else {
              this.selectedStair.userData.config.width -= spriteMove.length();
            }
          }
            break;
        }

        const edgeLength = Math.sqrt(
          Math.pow((config.firstPoint.x - config.secondPoint.x), 2) + Math.pow((config.firstPoint.y - config.secondPoint.y), 2)
        );

        spriteMove.setLength(spriteMove.length() / 2);

        this.sprites[0].position.add(spriteMove);
        if (!((config.width < this.constants.minimalStairsWidth) ||
          (config.width / 2 + config.distance + this.minimalSpaceForStairsOnSIde > edgeLength) ||
          (config.width / 2 + this.minimalSpaceForStairsOnSIde > config.distance) ||
          (0 > config.distance) ||
          (0 > config.width))) {

          // this.currentSprite.position.add(shift);
          // this.currentSprite.position.add(shift);

          // console.log('this.selectedStair', this.selectedStair);
          // this.selectedStair.parent.remove(this.selectedStair);
          // this.straightStairService.build(config);
          for (let i = 0; i < this.selectedStair.children.length; i++) {
            this.selectedStair.children[i].position.add(spriteMove);
            switch (this.currentSprite.name) {
              case 'Left sprite': {
                if (spriteMove.x * direction.x + spriteMove.z * direction.z > 0) {
                  this.selectedStair.children[i].scale.x = (this.widthSprites[0].position.distanceTo(this.widthSprites[1].position)) /
                    this.constants.inermediateStepSize.width;
                } else {
                  this.selectedStair.children[i].scale.x = (this.widthSprites[0].position.distanceTo(this.widthSprites[1].position)) /
                    this.constants.inermediateStepSize.width;
                }
              }
                break;
              case 'Right sprite': {
                if (spriteMove.x * direction.x + spriteMove.z * direction.z > 0) {
                  this.selectedStair.children[i].scale.x = (this.widthSprites[0].position.distanceTo(this.widthSprites[1].position)) /
                    this.constants.inermediateStepSize.width;
                } else {
                  this.selectedStair.children[i].scale.x = (this.widthSprites[0].position.distanceTo(this.widthSprites[1].position)) /
                    this.constants.inermediateStepSize.width;
                }
              }
                break;
            }
          }

        } else {
          this.currentSprite.position.set(oldSpritePosition.x, oldSpritePosition.y, oldSpritePosition.z);
          spriteMove.negate();
          shift.negate();
          this.sprites[0].position.add(spriteMove);

          if (spriteMove.x * direction.x + spriteMove.z * direction.z > 0) {
            this.selectedStair.userData.config.distance += spriteMove.length();
          } else {
            this.selectedStair.userData.config.distance -= spriteMove.length();
          }

          switch (this.currentSprite.name) {
            case 'Left sprite': {
              if (spriteMove.x * direction.x + spriteMove.z * direction.z > 0) {
                this.selectedStair.userData.config.width -= spriteMove.length() * 2;
              } else {
                this.selectedStair.userData.config.width += spriteMove.length() * 2;
              }
            }
              break;
            case 'Right sprite': {
              if (spriteMove.x * direction.x + spriteMove.z * direction.z > 0) {
                this.selectedStair.userData.config.width += spriteMove.length() * 2;
              } else {
                this.selectedStair.userData.config.width -= spriteMove.length() * 2;
              }
            }
              break;
          }
        }
        this.oldPosition = this.newPosition;
      }
      this.stairDimensionsSrvc.update(this.selectedStair);
    }
  }

  mouseUp(e) {
    if (this.appSettingsService.is2D) {
      this.moveCameraService.enable();
    }
    if (this.currentSprite) {
      // if(!this.minimalSpaceForStairsOnSIde){
        this.checkDistances()
      // }
      this.currentSprite = null;
      this.removeSprites(true);
      const config = this.selectedStair.userData.config;
      this.drawSprites(this.selectedStair);
      config.firstPoint.distance = config.distance;
      config.firstPoint.width = config.width;
      this.railingGeneration.buildRailing(this.railingGeneration.IsVisible());
      this.undoSrvc.dispatch(this.stairsTransformAction);
    }
  }

  private checkDistances() {
    const fp = new THREE.Vector3(this.selectedStair.userData.config.firstPoint.x, 0, this.selectedStair.userData.config.firstPoint.y);
    const sp = new THREE.Vector3(this.selectedStair.userData.config.secondPoint.x, 0, this.selectedStair.userData.config.secondPoint.y);
    const edgeWidth = fp.distanceTo(sp)
    const stairRebuildData = this.checkOffset(this.currentSprite.name, edgeWidth, this.selectedStair.userData.config.width, this.selectedStair.userData.config.distance, fp, sp);
    if (stairRebuildData.needStairRebuild) {
      if (this.currentSprite.name === 'Left sprite') {
        this.selectedStair.userData.config.distance += stairRebuildData.alignToCorner ? stairRebuildData.offset / 2 : -stairRebuildData.offset / 2;
        this.selectedStair.userData.config.width += stairRebuildData.alignToCorner ? -stairRebuildData.offset : stairRebuildData.offset;
      } else if (this.currentSprite.name === 'Right sprite') {
        this.selectedStair.userData.config.distance += stairRebuildData.alignToCorner ? stairRebuildData.offset / 2 : -stairRebuildData.offset / 2;
        this.selectedStair.userData.config.width += stairRebuildData.alignToCorner ? stairRebuildData.offset : -stairRebuildData.offset;
      } else if(this.currentSprite.name === 'Top sprite'){
        this.selectedStair.userData.config.distance += stairRebuildData.alignToCorner ? stairRebuildData.offset : -stairRebuildData.offset ;
      }
      this.selectedStair.children.forEach(child => {
        child.scale.x = this.selectedStair.userData.config.width / this.constants.inermediateStepSize.width;
        child.position.add(stairRebuildData.offsetVector);
      })
      this.sprites.forEach(sprite => {
        sprite.position.add(stairRebuildData.offsetVector);
      })
      this.stairDimensionsSrvc.update(this.selectedStair);
    }
  }

  private checkOffset(spriteName, edgeWidth, stairWidth, distance, fp, sp) {
    let offset = 0, direction, needStairRebuild = false, alignToCorner = false;
    if (spriteName === 'Right sprite') {
      const distanceToCorner = edgeWidth - (distance + stairWidth / 2);
      if (distanceToCorner < 15) {
        direction = new THREE.Vector3(sp.x - fp.x, 0, sp.z - fp.z)
        offset = distanceToCorner;
        needStairRebuild = true;
        alignToCorner = true;
        direction.setLength(offset / 2);
      } else if (distanceToCorner < 30) {
        direction = new THREE.Vector3(sp.x - fp.x, 0, sp.z - fp.z)
        offset = 30 - distanceToCorner;
        needStairRebuild = true;
        direction.setLength(-offset / 2);
      }
    } else if (spriteName === 'Left sprite') {
      const distanceToCorner = distance - stairWidth / 2;
      direction = new THREE.Vector3(fp.x - sp.x, 0, fp.z - sp.z);
      if (distanceToCorner < 15) {
        offset = -distanceToCorner;
        needStairRebuild = true;
        alignToCorner = true
        direction.setLength(-offset / 2);
      } else if (distanceToCorner < 30) {
        offset = distanceToCorner - 30;
        needStairRebuild = true;
        direction.setLength(offset / 2)
      }
    } else if (this.currentSprite.name === 'Top sprite') {
      const distanceToSpCorner = edgeWidth - (distance + stairWidth / 2);
      const distanceToFpCorner = distance - stairWidth / 2;
      needStairRebuild = distanceToSpCorner < 30 || distanceToFpCorner < 30 ? true : false;
      direction = new THREE.Vector3(sp.x - fp.x, 0, sp.z - fp.z)
      if (needStairRebuild) {
        if (distanceToSpCorner < 15 && distanceToSpCorner<distanceToFpCorner ) {
          offset = distanceToSpCorner;
          alignToCorner = true;
          direction.setLength(offset );
        } else if (distanceToSpCorner < 30  && distanceToFpCorner > 15) {
          offset = 30 - distanceToSpCorner;
          direction.setLength(-offset );
        }
        if (distanceToFpCorner < 15 && distanceToSpCorner>distanceToFpCorner ) {
          offset = -distanceToFpCorner;
          alignToCorner = true
          direction.setLength(offset);
        }
        else if (distanceToFpCorner < 30 && distanceToSpCorner > 15) {
          offset = distanceToFpCorner - 30;
          direction.setLength(-offset)
        }

      }
    }
    return {
      offset: offset,
      offsetVector: direction,
      needStairRebuild: needStairRebuild,
      alignToCorner: alignToCorner
    }
  }

  disable() {
    this._stairResizeTouchId = null;
    this._suppressNextMouseDownAfterTouch = false;
    this.removeSprites();
    this.selectStairSubscription && this.selectStairSubscription.unsubscribe();
    super.disable();
  }

  enable() {
    super.enable();
    if (!this.selectStairSubscription || this.selectStairSubscription.isStopped) {
      this.selectStairSubscription = this.select3dService.selectedStairSubject.subscribe(selectedStairs => {
        this.removeSprites(true);
        this.drawSprites();
      });
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/services/move-stairs.service.ts